/**
 * Bekleyen Maaş Hareketi Onay Kartı — BOSS feed.
 *
 * Yetkili personelin (yönetici) eklediği ekleme/kesinti hareketleri
 * burada listelenir. BOSS onaylar (maaşa yansır) veya reddeder (silinir).
 * `?maasHareket=ID` ile gelen satır vurgulanır.
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Wallet, Plus, Minus, Check, X, Loader2 } from 'lucide-react'
import {
  bekleyenMaasHareketleri,
  maasHareketiOnayla,
  maasHareketiReddet,
  type MaasHareketi,
} from '@/lib/services/maas-hareket-service'

interface Props {
  onaylayanId: string
  onYenile?: () => void
}

function tl(n: number): string {
  return Math.round(n).toLocaleString('tr-TR')
}

export function BekleyenMaasHareketiKart({ onaylayanId, onYenile }: Props) {
  const [liste, setListe] = useState<MaasHareketi[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [islemId, setIslemId] = useState<string | null>(null)
  const [params] = useSearchParams()
  const vurgulananId = params.get('maasHareket')
  const kartRef = useRef<HTMLDivElement>(null)

  const yukle = useCallback(async () => {
    setYukleniyor(true)
    try {
      setListe(await bekleyenMaasHareketleri())
    } catch (e) {
      console.warn('[bekleyenMaasHareketi] yüklenemedi:', e)
    } finally {
      setYukleniyor(false)
    }
  }, [])

  useEffect(() => {
    void yukle()
  }, [yukle])

  // Bildirime tıklayınca (?maasHareket=ID) bu karta otomatik kaydır.
  // Sayfa/şube listesi geç render olabildiği için küçük gecikme + üste hizala.
  useEffect(() => {
    if (!yukleniyor && vurgulananId && liste.some((h) => h.id === vurgulananId)) {
      const t = setTimeout(() => {
        kartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 250)
      return () => clearTimeout(t)
    }
  }, [yukleniyor, vurgulananId, liste])

  async function onayla(h: MaasHareketi) {
    setIslemId(h.id)
    try {
      await maasHareketiOnayla(h.id, onaylayanId)
      setListe((l) => l.filter((x) => x.id !== h.id))
      onYenile?.()
    } catch (e) {
      alert((e as Error).message)
    } finally {
      setIslemId(null)
    }
  }

  async function reddet(h: MaasHareketi) {
    if (!confirm(`${h.personelIsim ?? 'Personel'} için ${tl(h.tutar)} ₺ ${h.tip} reddedilsin mi? Hareket silinecek.`)) return
    setIslemId(h.id)
    try {
      await maasHareketiReddet(h.id, onaylayanId)
      setListe((l) => l.filter((x) => x.id !== h.id))
      onYenile?.()
    } catch (e) {
      alert((e as Error).message)
    } finally {
      setIslemId(null)
    }
  }

  if (yukleniyor) return null
  if (liste.length === 0) return null

  return (
    <div ref={kartRef} className="scroll-mt-20 bg-white rounded-2xl border border-amber-300 shadow-sm overflow-hidden mb-4">
      <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
        <Wallet className="w-5 h-5 text-amber-700" />
        <h3 className="font-bold text-amber-900 text-sm">
          Onay bekleyen maaş hareketleri
        </h3>
        <span className="ml-auto text-xs font-bold bg-amber-600 text-white px-2 py-0.5 rounded-full">
          {liste.length}
        </span>
      </div>

      <div className="divide-y divide-gray-100">
        {liste.map((h) => {
          const ekleme = h.tip === 'ekleme'
          const vurgulu = h.id === vurgulananId
          const mesgul = islemId === h.id
          return (
            <div
              key={h.id}
              className={`px-4 py-3 ${vurgulu ? 'bg-amber-50 ring-2 ring-amber-400 ring-inset' : ''}`}
            >
              <div className="flex items-start gap-2">
                <div className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  ekleme ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {ekleme ? <Plus className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm truncate">
                      {h.personelIsim || 'Personel'}
                    </span>
                    <span className={`font-bold text-sm ${ekleme ? 'text-green-700' : 'text-red-700'}`}>
                      {ekleme ? '+' : '−'}{tl(h.tutar)} ₺
                    </span>
                    <span className="text-[10px] text-gray-500">
                      {h.donem} · {ekleme ? 'Ekleme' : 'Kesinti'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-700 mt-0.5 break-words">{h.aciklama}</p>
                </div>
              </div>

              <div className="flex gap-2 mt-2.5 pl-9">
                <button
                  onClick={() => onayla(h)}
                  disabled={mesgul}
                  className="flex-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-1"
                >
                  {mesgul ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Onayla
                </button>
                <button
                  onClick={() => reddet(h)}
                  disabled={mesgul}
                  className="flex-1 px-3 py-1.5 bg-white border border-red-300 hover:bg-red-50 disabled:opacity-50 text-red-700 rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-1"
                >
                  <X className="w-3.5 h-3.5" />
                  Reddet
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
